import { readQueryParams } from 'src/utils/QueryParamsHelper';
import { FEEDBACK_HISTORY_TABLE } from '../../constants/Overview.constant';
import { FeedbackHistoryPayload } from '../../interfaces/Overview.type';
import {
  FEEDBACK_FORM_STATES,
  FeedbackFormAction,
  FeedbackFormKind,
  IFeedbackForm,
  Option
} from '../interfaces/index.type';
import { SESSION_STORAGE_KEYS } from '../../../../constants/StorageKeys';
import { pushToSessionStorage } from '../../../../utils/StorageUtils';
import styles from '../index.module.scss';

export const getSortedOption = (options: Array<Option>) => {
  const sortedOptions = [...options];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //    @ts-ignore
  return sortedOptions.sort((option1: Option, option2: Option) =>
    option1.sortOrder < option2.sortOrder ? -1 : option1.sortOrder > option2.sortOrder ? 1 : 0
  );
};

export const feedbackFromReducer = (state: IFeedbackForm, action: FeedbackFormAction) => {
  const { type, payload } = action;
  switch (type) {
    case FeedbackFormKind.SET_SELECTED_CHIP:
      return {
        ...state,
        selectedChip: payload?.selectedChip || '',
        initialOptionId: payload?.initialOptionId || ''
      };
    case FeedbackFormKind.REMOVE_SELECTED_CHIP:
      return {
        ...state,
        selectedChip: null,
        questionMap: null,
        error: null,
        comment: '',
        loading: false,
        telephoneNotRequired: false
      };
    case FeedbackFormKind.UPDATE_QUESTION_MAP:
      return {
        ...state,
        questionMap: payload?.questionResponse || null,
        loading: false
      };
    case FeedbackFormKind.HANDLE_FORM_ERROR:
      return {
        ...state,
        error: payload?.error || null,
        loading: false
      };
    case FeedbackFormKind.UPDATE_TELEPHONES: {
      return {
        ...state,
        telephone: payload?.telephone,
        isDefaultCallRequired: payload?.isDefaultCallRequired,
        loading: false
      };
    }
    case FeedbackFormKind.UPDATE_COMMENT: {
      return {
        ...state,
        comment: payload?.comment,
        loading: false
      };
    }
    case FeedbackFormKind.BEFORE_API_CALL:
      return {
        ...state,
        loading: true
      };
    case FeedbackFormKind.SUCESSFUL_API_CALL:
      return {
        ...state,
        loading: false,
        selectedChip: null,
        questionMap: null,
        error: null,
        comment: '',
        feedbackFormState: FEEDBACK_FORM_STATES.SUBMITED,
        telephoneNotRequired: false,
        telephone: payload?.telephone || ''
      };
    case FeedbackFormKind.SET_LOADING:
      return {
        ...state,
        loading: payload?.loading
      };
    case FeedbackFormKind.SET_INITIAL_QUESTION_ID:
      return {
        ...state,
        initialQuestionId: payload?.initialQuestionId || ''
      };
    case FeedbackFormKind.SET_INTERACTION_ID:
      if (payload?.interactionId) {
        pushToSessionStorage(
          SESSION_STORAGE_KEYS.INTERACTION_ID,
          JSON.stringify(payload.interactionId)
        );
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.INTERACTION_ID);
      }
      return {
        ...state,
        interactionId: payload?.interactionId || null
      };
    case FeedbackFormKind.SET_FEEDBACK_FORM_STATE: {
      return {
        ...state,
        feedbackFormState: payload?.feedbackFormState
      };
    }
    case FeedbackFormKind.API_ERROR: {
      return {
        ...state,
        loading: false
      };
    }
    case FeedbackFormKind.ImageUploadStatus: {
      return {
        ...state,
        isImageUpload: payload?.isImageUpload
      };
    }
    case FeedbackFormKind.RESET_STATE: {
      return {
        ...state,
        selectedChip: null,
        questionMap: null,
        error: null,
        comment: '',
        loading: false,
        initialOptionId: '',
        initialQuestionId: '',
        interactionId: null,
        feedbackFormState: FEEDBACK_FORM_STATES.NOT_SUBMITED,
        isImageUpload: false
      };
    }
    case FeedbackFormKind.TELEPHONE_REQUIRED: {
      if (payload?.telephoneNotRequired) {
        return {
          ...state,
          telephoneNotRequired: payload?.telephoneNotRequired,
          telephone: ''
        };
      }
      return {
        ...state,
        telephoneNotRequired: payload?.telephoneNotRequired,
        telephone: payload?.telephone ? payload?.telephone : state.telephone
      };
    }
    default:
      return state;
  }
};

export const createPayloadForFeedbackHistory = (
  loanId: string,
  caseReferenceId: string,
  caseBusinessVertical: string
) => {
  const { [FEEDBACK_HISTORY_TABLE]: queryParams } = readQueryParams();
  return {
    page: queryParams?.page || 0,
    size: queryParams?.size || 10,
    loan_account_number: loanId,
    customer_reachable: queryParams?.contactable,
    caseReferenceId: caseReferenceId,
    caseBusinessVertical: caseBusinessVertical
  } as FeedbackHistoryPayload;
};

export const mapQuestionToRadioOptions = (questionArray: Option[], handleOutside = false) => {
  if (handleOutside) {
    return questionArray?.map(item => ({
      id: item.text,
      label: item.text,
      value: item.text,
      checked: item?.metadata?.isDefault
    }));
  }
  return questionArray?.map(item => ({
    id: item.referenceId,
    label: item.text,
    value: item.referenceId,
    checked: item?.metadata?.isDefault
  }));
};

export const InteractionsEnum = {
  HOVER: 'hover',
  ACTIVE_CHIP: 'active-chip',
  ACTIVE_CANCEL_ICON: 'active-cancel'
};

type Interactions = (typeof InteractionsEnum)[keyof typeof InteractionsEnum];

const stylesForChip = [
  {
    [InteractionsEnum.HOVER]: styles.redHover,
    [InteractionsEnum.ACTIVE_CHIP]: styles.red,
    [InteractionsEnum.ACTIVE_CANCEL_ICON]: 'var(--navi-color-red-base)'
  },
  {
    [InteractionsEnum.HOVER]: styles.orangeHover,
    [InteractionsEnum.ACTIVE_CHIP]: styles.orange,
    [InteractionsEnum.ACTIVE_CANCEL_ICON]: 'var(--navi-color-orange-base)'
  },
  {
    [InteractionsEnum.HOVER]: styles.greenHover,
    [InteractionsEnum.ACTIVE_CHIP]: styles.green,
    [InteractionsEnum.ACTIVE_CANCEL_ICON]: 'var(--navi-color-green-base)'
  },
  {
    [InteractionsEnum.HOVER]: styles.yellowHover,
    [InteractionsEnum.ACTIVE_CHIP]: styles.yellow,
    [InteractionsEnum.ACTIVE_CANCEL_ICON]: 'var(--navi-color-yellow-dark)'
  },
  {
    [InteractionsEnum.HOVER]: styles.blueHover,
    [InteractionsEnum.ACTIVE_CHIP]: styles.blue,
    [InteractionsEnum.ACTIVE_CANCEL_ICON]: 'var(--navi-color-blue-base)'
  }
];

export const getStylesForChipInteractions = (
  interactionLevel: number | undefined,
  interaction: Interactions
) => {
  if (!interactionLevel || interactionLevel > stylesForChip.length || interactionLevel < 1) {
    return stylesForChip[stylesForChip.length - 1][interaction];
  }

  return stylesForChip[interactionLevel - 1][interaction];
};

export const selectedOptionChipStyles = (interactionLevel: number | undefined) => {
  if (interactionLevel === 1) {
    return styles.red;
  }
  if (interactionLevel === 2) {
    return styles.orange;
  }
  if (interactionLevel === 3) {
    return styles.green;
  }
  if (interactionLevel === 4) {
    return styles.yellow;
  }
  return styles.blue;
};

export const selectedAdornmentStyles = (interactionLevel: number | undefined) => {
  if (interactionLevel === 1) {
    return 'var(--navi-color-red-base)';
  }
  if (interactionLevel === 2) {
    return `var(--navi-color-orange-base)`;
  }
  if (interactionLevel === 3) {
    return `var(--navi-color-green-base)`;
  }
  return `var(--navi-color-blue-base)`;
};
